/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import { STARCTypography } from '@/components/STARCTypography';
import { pageTypes } from '@/constants/page';
import { CMSNullResult } from '@/features/contentstack/components/CMSNullResult/CMSNullResult';
import { CMSProductFinder } from '@/features/contentstack/components/CMSProductFinder/CMSProductFinder';
import { CMSYmme } from '@/features/contentstack/components/CMSYmme/CMSYmme';
import { useCMSProductFinderAttributes } from '@/features/contentstack/hooks/useCMSProductFinderAttributes';
import type {
  ContentStackNullResult,
  ContentStackProductFinder,
  ContentStackYmme,
} from '@/features/contentstack/interface';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

type Props = {
  content: Omit<ContentStackYmme, 'theme' | 'title'>;
  productFinderData?: ContentStackProductFinder;
  nullResultsData?: ContentStackNullResult;
  makeModelYearPageType?: boolean;
  pageType?: string;
};

const CMSShelfYmme = ({
  productFinderData,
  nullResultsData,
  makeModelYearPageType,
  pageType = pageTypes.shelf,
}: Props) => {
  const { specificationsAttributes } = useCMSProductFinderAttributes();
  const { acesEnabled } = useAcesFlags();
  const preferredVehicle = usePreferredVehicle();

  const meetsCatalogRequirement = !acesEnabled || (!makeModelYearPageType && acesEnabled);

  const hasSpecificationsAttributes = (specificationsAttributes?.length || 0) > 0;

  const shouldRenderProductFinder =
    !!preferredVehicle && !!productFinderData && hasSpecificationsAttributes;

  const shouldRenderYmme =
    meetsCatalogRequirement && (!preferredVehicle || shouldRenderProductFinder);

  return (
    <>
      {shouldRenderYmme && (
        <CMSYmme removeMargins={shouldRenderProductFinder} pageType={pageType} />
      )}

      {shouldRenderProductFinder && <CMSProductFinder content={productFinderData} />}

      {nullResultsData && (
        <STARCTypography>
          <CMSNullResult content={nullResultsData} />
        </STARCTypography>
      )}
    </>
  );
};

export { CMSShelfYmme };
